// AI-CONTEXT: See docs/features/impact-in-beeld-fase2.md
import { v4 as uuid } from 'uuid'
import { buildPublicationSnapshot } from './buildPublicationSnapshot'

/**
 * Publiceren = bevriezen: schrijft in één batch (a) de org-publicatie, (b) één
 * publicatie per meegegeven product (overschrijft per doel — nooit duplicaten)
 * en (c) zet het kanaal op 'live'. Zonder presenteerbaar kengetal wordt er
 * NIETS geschreven en krijgt de caller de methodiek-reden terug.
 *
 * @param {object} input
 * @param {object} input.db - Firestore client SDK (firebase/config db)
 * @param {string} input.client - Tenant-CompagnyID
 * @param {string} input.userId - Auth-ID van de publicerende gebruiker
 * @param {*} input.publishedAt - Timestamp-waarde voor PublishedAt (serverTimestamp van de caller)
 * @param {{result: object|null, label: string|null, totalValue: number|null, branding: object}} input.snapshotBase - Invoer voor buildPublicationSnapshot
 * @param {Array<object>} input.products - Producten waarvoor een productpagina gepubliceerd wordt ([] voor org-only kanalen)
 * @param {Array<{docid: string, Target: string, ProductID?: string}>} input.existingPublications - Huidige ImpactPublications-docs (centrale hook)
 * @param {{docid: string, config: object}} input.channel - ImpactChannels-doc dat live gaat + zijn config
 * @returns {Promise<{blocked: boolean, reden?: string}>} blocked=true zonder kengetal (geen write); write-fouten gooien door
 */
const publishImpactPages = async ({
    db,
    client,
    userId,
    publishedAt,
    snapshotBase,
    products,
    existingPublications,
    channel,
}) => {
    const orgSnapshot = buildPublicationSnapshot(snapshotBase)
    if (!orgSnapshot) {
        return { blocked: true, reden: snapshotBase?.result?.reden || 'Geen presenteerbaar kengetal.' }
    }

    const existing = Array.isArray(existingPublications) ? existingPublications : []
    const publications = db.collection('ImpactPublications')
    const batch = db.batch()

    let nextPosition = existing
        .reduce((max, publication) => Math.max(max, Number(publication.Position) || 0), 0) + 1

    const writePublication = (target, snapshot, productFields, existingDoc) => {
        const payload = {
            Snapshot: snapshot,
            PublishedAt: publishedAt,
            PublishedBy: userId,
        }

        if (existingDoc) {
            batch.update(publications.doc(existingDoc.docid), payload)
            return
        }

        batch.set(publications.doc(), {
            ID: uuid(),
            CompagnyID: client,
            Compagny: client,
            Position: nextPosition++,
            Timestamp: publishedAt,
            Target: target,
            ScanCount: 0,
            ...productFields,
            ...payload,
        })
    }

    writePublication(
        'org',
        orgSnapshot,
        {},
        existing.find((publication) => publication.Target === 'org'),
    )

    ;(products || []).forEach((product) => {
        const snapshot = buildPublicationSnapshot({ ...snapshotBase, product })
        writePublication(
            'product',
            snapshot,
            { ProductID: product.ID, Slug: product.Slug },
            existing.find((publication) =>
                publication.Target === 'product' && publication.ProductID === product.ID),
        )
    })

    batch.update(db.collection('ImpactChannels').doc(channel.docid), {
        Status: 'live',
        PublishedAt: publishedAt,
        Config: channel.config || {},
    })

    await batch.commit()

    return { blocked: false }
}

export { publishImpactPages }
